/*
 * Button Component
 * ---
 *
 * @param props.class {String} - takes in css classes and passes them back down to the component
 * @param props.label {String} - sets the text within the button
 *
 */

import React from "react";
import CartIcon from "./CartIcon";
import HeartIcon from "../Icons/HeartIcon";
import Arrow from "./Arrow";
import BellIcon from "../Icons/BellIcon";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

type Variant = "primary" | "secondary" | "tertiary" | "underline" | "plainUnderline";

type AvailableIcons = "cart" | "heart" | "bell";

interface IButtonProps {
  children: string;
  fullWidth?: boolean;
  isSubmitButton?: boolean;
  disabled?: boolean;
  variant?: Variant;
  small?: boolean;
  beforeIcon?: AvailableIcons;
  className?: string;
}

interface IBaseButton extends IButtonProps {
  mode: "button";
  onClick: () => void;
  type?: "submit";
  showSpinner?: boolean;
  spinnerClasses?: string;
}

interface IBaseLink extends IButtonProps {
  mode: "link";
  href: string;
}

const button: { [K: string]: string} = {
  small: "px-[16px] py-[8px] text-desktop-caption rounded-full ",
  default:
    "flex items-center justify-center px-[40px] py-[20px] h-max rounded-full text-desktop-base font-secondary font-bold w-full disabled:text-white disabled:hover:text-white disabled:hover:bg-grey-400 ",
  primary:
    "bg-secondary-yellow hover:bg-black hover:text-white disabled:bg-grey-400 group ",
  secondary:
    "border-1 bg-white disabled:border-grey-400 disabled:bg-white disabled:text-grey-400 hover:bg-black hover:text-white",
  tertiary:
    "flex items-center py-0 px-0 hover:bg-white hover:text-grey-600 disabled:bg-white disabled:text-grey-400",
  underline:
    "px-0 py-0 bg-white text-black relative hover:text-grey-600 after:block after:absolute after:w-full after:bottom-0 after:h-[1px] after:bg-black after:hover:bg-grey-600 disabled:text-grey-400 disabled:after:bg-grey-400",
  plainUnderline: "text-desktop-base font-secondary text-black hover:text-grey-600 border-b-1 border-black",
  fullWidth: "w-max",
  arrowDisabled: "stroke-black fill-black",
  arrowActive: "stroke-grey-400 fill-grey-400"
};

const addClasses = (shouldAdd:boolean, classes:string) => shouldAdd ? `${classes} ` : ""
const addVariantClasses = (variantType:string) => button[variantType] ? `${button[variantType]} ` : ""

export const Button = (props: IBaseButton | IBaseLink) => {
  if (props.mode === "button") {
    const {
      children,
      variant = "primary",
      disabled,
      type = props.isSubmitButton ? "submit" : "button",
      onClick,
      small,
      fullWidth,
      isSubmitButton,
      beforeIcon,
      className,
      showSpinner = false,
      spinnerClasses = "",
    } = props;

    let classNames = ''
    if(variant !== 'plainUnderline') {
      classNames += button.small
      classNames += addClasses(!small, button.default)
    }
    classNames += addVariantClasses(variant)
    classNames += addClasses(!fullWidth, button.fullWidth)
    classNames += addClasses(showSpinner, "py-[9px]")
    classNames += className ? className : ""

    if (isSubmitButton) classNames = classNames.replace(/py-\[8px\]/i, '');

    return (
      <button
        disabled={disabled}
        onClick={onClick}
        type={type}
        className={classNames}
      >
        {beforeIcon === "cart" && <CartIcon hasHover className="mr-[10px]" />}
        {beforeIcon === "bell" && <BellIcon hasHover className="mr-[10px]" />}
        {showSpinner ? (
          <LoadingSpinner size={"large"} className={spinnerClasses} />
        ) : (
          children
        )}
        {variant === "tertiary" && (
          <Arrow
            className="ml-[8px] rotate-90"
            strokeClassName={
              disabled ? button.arrowDisabled : button.arrowActive
            }
          />
        )}
      </button>
    );
  }

  const {
    children,
    href,
    variant,
    disabled,
    small,
    fullWidth,
    beforeIcon,
    className,
  } = props;

  let classNames = ''
  classNames += button.small
  classNames += addClasses(!small, button.default)
  classNames += addVariantClasses(String(variant))
  classNames += addClasses(!fullWidth, button.fullWidth)
  classNames += "flex flex-row gap-[8px] "
  classNames += className ? className : ""

  return (
    <a
      href={href}
      className={classNames}
    >
      {beforeIcon === "heart" && <HeartIcon className="mr-[8px]" />}
      {beforeIcon === "bell" && <BellIcon hasHover className="mr-[8px]" />}
      {children}
      {variant === "tertiary" && (
        <Arrow
          className="rotate-90"
          strokeClassName={disabled ? button.arrowDisabled : button.arrowActive}
        />
      )}
    </a>
  );
};

//export default Button;
